import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, resolveMedia } from "../lib/api";
import { openWhatsApp } from "../lib/whatsapp";
import { MapPin, Clock, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DAYS = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];
const CATS = ["Todos","Gastronomía","Cultura","Bienestar","Privado"];

const buildWhatsAppMessage = (ev) =>
  `Hola. Me interesa "${ev.title}" el ${ev.date} a las ${ev.time}. ¿Me confirman disponibilidad?`;

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [cat, setCat] = useState("Todos");
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [sheetDay, setSheetDay] = useState(null); // { day, events } for mobile day picker

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => events.filter((e) => cat === "Todos" || e.category === cat), [events, cat]);

  const inMonth = useMemo(() =>
    filtered.filter((e) => {
      const d = new Date(e.date + "T00:00:00");
      return d.getFullYear() === month.y && d.getMonth() === month.m;
    }), [filtered, month]);

  const grid = useMemo(() => {
    const first = new Date(month.y, month.m, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [month]);

  const eventsForDay = (day) => inMonth.filter((e) => new Date(e.date + "T00:00:00").getDate() === day);

  const changeMonth = (delta) => {
    setMonth((m) => {
      const d = new Date(m.y, m.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  const handleCta = (ev, e) => {
    if (e) e.preventDefault();
    if (ev.cta_type === "whatsapp") {
      openWhatsApp(buildWhatsAppMessage(ev));
    } else if (ev.cta_type === "external" && ev.cta_url) {
      window.open(ev.cta_url, "_blank", "noopener,noreferrer");
    }
  };

  const hasCta = (ev) => (ev.cta_type === "whatsapp" || (ev.cta_type === "external" && ev.cta_url)) && !!ev.cta_label;

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16" data-testid="calendar-page">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-8">
          <p className="eyebrow">Agenda · Valle</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-6xl leading-[1.02] text-cobalt-deep">Lo que vale la pena poner en <span className="serif-italic text-cobalt">su calendario.</span></h1>
          <p className="mt-6 text-[1rem] text-muted2 leading-relaxed max-w-2xl">
            Cenas de autor, cavas privadas, retiros discretos y algún concierto de jardín. Todo curado, en cifras limitadas.
          </p>
        </div>
      </div>

      {/* Filters + month controls */}
      <div className="mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8 border-b border-cobalt/40 pb-6">
        <div className="flex flex-wrap gap-6 sm:gap-8">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`cal-cat-${c}`}
              className={`font-display text-[0.68rem] tracking-[0.24em] uppercase pb-2 font-semibold transition-colors ${cat === c ? "text-cobalt border-b border-cobalt" : "text-ink/55 hover:text-cobalt"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-6">
          <button onClick={() => changeMonth(-1)} aria-label="Mes anterior" data-testid="cal-prev" className="text-cobalt hover:text-terracotta"><ChevronLeft size={22} /></button>
          <p className="font-serif text-3xl text-cobalt-deep tracking-tight min-w-[240px] text-center">{MONTHS[month.m]} <span className="serif-italic text-cobalt">{month.y}</span></p>
          <button onClick={() => changeMonth(1)} aria-label="Mes siguiente" data-testid="cal-next" className="text-cobalt hover:text-terracotta"><ChevronRight size={22} /></button>
        </div>
      </div>

      {/* Month grid — compact dots on mobile, full cards on md+ */}
      <div className="mt-10 grid grid-cols-7 border-l border-t border-cobalt/40">
        {DAYS.map((d) => (
          <div key={d} className="border-r border-b border-cobalt/40 bg-cream px-2 md:px-3 py-1.5 md:py-2 text-[0.55rem] md:text-[0.65rem] tracking-[0.22em] md:tracking-[0.28em] uppercase font-display font-semibold text-cobalt text-center md:text-left">{d}</div>
        ))}
        {grid.map((day, i) => {
          const dayEvents = day ? eventsForDay(day) : [];
          const isToday = day && new Date().getFullYear() === month.y && new Date().getMonth() === month.m && new Date().getDate() === day;
          return (
            <div key={i} className={`border-r border-b border-cobalt/40 min-h-[72px] md:min-h-[140px] p-1.5 md:p-3 bg-cream ${dayEvents.length ? 'relative' : ''}`}>
              {day && (
                <>
                  <p className={`font-serif text-base md:text-xl ${isToday ? 'text-terracotta' : 'text-cobalt-deep'} leading-tight`}>
                    {String(day).padStart(2, '0')}
                    {isToday && <span className="hidden md:inline ml-2 script text-lg text-terracotta">hoy</span>}
                  </p>

                  {/* Mobile: compact dot/count indicator — tap opens day picker sheet */}
                  <div className="md:hidden mt-1">
                    {dayEvents.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSheetDay({ day, events: dayEvents })}
                        data-testid={`cal-mobile-day-${day}`}
                        className="flex flex-wrap items-center gap-0.5 pt-0.5 -mx-1.5 -mt-1 px-1.5 pt-1 pb-1 w-[calc(100%+0.75rem)] text-left active:bg-cobalt/5"
                        aria-label={`${dayEvents.length} eventos el ${day}`}
                      >
                        {dayEvents.slice(0, 3).map((e) => (
                          <span key={e.id} className="inline-block w-1.5 h-1.5 rounded-full bg-cobalt" />
                        ))}
                        {dayEvents.length > 3 && (
                          <span className="ml-0.5 text-[0.55rem] font-display font-semibold text-cobalt">+{dayEvents.length - 3}</span>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Desktop: full event titles + hover cards */}
                  <div className="hidden md:block mt-2 space-y-1.5">
                    {dayEvents.slice(0, 3).map((e) => (
                      <HoverCard key={e.id} openDelay={120} closeDelay={120}>
                        <HoverCardTrigger asChild>
                          <Link
                            to={`/calendario/${e.id}`}
                            data-testid={`cal-event-${e.id}`}
                            className="block w-full text-left group"
                          >
                            <span className="block border-l-2 border-cobalt pl-2 py-0.5 text-[0.72rem] leading-tight text-cobalt-deep group-hover:text-terracotta group-hover:border-terracotta transition-colors">
                              <span className="block text-[0.55rem] tracking-[0.28em] uppercase text-cobalt/70 font-display font-semibold">{e.time}</span>
                              <span className="block truncate">{e.title}</span>
                            </span>
                          </Link>
                        </HoverCardTrigger>
                        <HoverCardContent
                          side="right"
                          align="start"
                          sideOffset={10}
                          className="w-72 p-0 bg-white border border-cobalt/40 rounded-none shadow-xl"
                          data-testid={`cal-hover-${e.id}`}
                        >
                          {e.image && (
                            <img src={resolveMedia(e.image)} alt={e.title} className="w-full h-36 object-cover" />
                          )}
                          <div className="p-4">
                            <p className="eyebrow">{e.category}</p>
                            <Link to={`/calendario/${e.id}`} className="block mt-2 font-serif text-lg text-ink leading-tight hover:text-cobalt transition-colors" data-testid={`cal-hover-title-${e.id}`}>
                              {e.title}
                            </Link>
                            {e.tagline && <p className="mt-1 script text-base text-cobalt leading-snug">{e.tagline}</p>}
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[0.6rem] font-display font-semibold tracking-[0.22em] uppercase text-cobalt">
                              <span className="inline-flex items-center gap-1.5"><Clock size={11}/> {e.time}</span>
                              <span className="inline-flex items-center gap-1.5"><MapPin size={11}/> {e.location}</span>
                            </div>
                            {e.price && <p className="mt-2 text-sm text-ink">{e.price}</p>}
                            {hasCta(e) && (
                              <button
                                onClick={(evt) => handleCta(e, evt)}
                                data-testid={`cal-hover-cta-${e.id}`}
                                className="mt-4 w-full btn-primary text-xs py-2.5 inline-flex items-center justify-center gap-1.5"
                              >
                                {e.cta_label}
                                {e.cta_type === "external" && <ArrowUpRight size={12} />}
                              </button>
                            )}
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    ))}
                    {dayEvents.length > 3 && (
                      <p className="text-[0.62rem] uppercase tracking-widest text-subtle pl-2 font-display font-semibold">+{dayEvents.length - 3} más</p>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile day picker sheet */}
      <Sheet open={!!sheetDay} onOpenChange={(open) => !open && setSheetDay(null)}>
        <SheetContent
          side="bottom"
          className="md:hidden rounded-t-xl border-t border-cobalt/40 bg-cream max-h-[85vh] overflow-y-auto p-0"
          data-testid="cal-day-sheet"
        >
          {sheetDay && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4 text-left border-b border-cobalt/20">
                <p className="eyebrow">{DAYS[((new Date(month.y, month.m, sheetDay.day).getDay())+6)%7]} · {MONTHS[month.m]}</p>
                <SheetTitle className="font-serif text-4xl text-cobalt-deep leading-none mt-1">
                  {String(sheetDay.day).padStart(2, '0')}
                  <span className="serif-italic text-cobalt text-2xl ml-2">
                    {sheetDay.events.length} {sheetDay.events.length === 1 ? 'evento' : 'eventos'}
                  </span>
                </SheetTitle>
              </SheetHeader>
              <div className="divide-y divide-cobalt/15">
                {sheetDay.events.map((e) => (
                  <article key={e.id} className="px-6 py-5" data-testid={`cal-sheet-event-${e.id}`}>
                    <Link
                      to={`/calendario/${e.id}`}
                      onClick={() => setSheetDay(null)}
                      className="flex gap-4 group"
                    >
                      {e.image && (
                        <img
                          src={resolveMedia(e.image)}
                          alt={e.title}
                          className="w-20 h-20 object-cover flex-shrink-0 rounded-sm"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="eyebrow">{e.category}</p>
                        <h3 className="mt-1 font-serif text-lg leading-tight text-cobalt-deep group-hover:text-terracotta transition-colors">
                          {e.title}
                        </h3>
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[0.6rem] font-display font-semibold tracking-[0.2em] uppercase text-cobalt">
                          <span className="inline-flex items-center gap-1"><Clock size={11}/> {e.time}</span>
                          <span className="inline-flex items-center gap-1"><MapPin size={11}/> {e.location}</span>
                        </div>
                        {e.price && <p className="mt-1.5 text-xs text-ink">{e.price}</p>}
                      </div>
                    </Link>
                    {hasCta(e) && (
                      <button
                        onClick={(evt) => { setSheetDay(null); handleCta(e, evt); }}
                        data-testid={`cal-sheet-cta-${e.id}`}
                        className="mt-3 w-full btn-primary text-xs py-2.5 inline-flex items-center justify-center gap-1.5"
                      >
                        {e.cta_label}
                        {e.cta_type === "external" && <ArrowUpRight size={12} />}
                      </button>
                    )}
                  </article>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Upcoming list */}
      <div className="mt-24">
        <div className="flex items-end justify-between border-b border-cobalt/40 pb-4">
          <div>
            <p className="eyebrow">Próximos</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-cobalt-deep">Lo que viene</h2>
          </div>
        </div>
        <div className="mt-10 space-y-12">
          {filtered.length === 0 && <p className="text-subtle">Sin eventos por ahora.</p>}
          {filtered.map((e, i) => {
            const d = new Date(e.date + "T00:00:00");
            return (
              <article key={e.id} className="grid grid-cols-1 md:grid-cols-12 gap-8 border-b border-cobalt/25 pb-12 fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`event-row-${e.id}`}>
                <div className="md:col-span-2">
                  <p className="font-serif text-6xl leading-none text-cobalt-deep">{d.getDate()}</p>
                  <p className="script text-2xl text-cobalt mt-2 leading-none">{MONTHS[d.getMonth()].toLowerCase()}</p>
                  <p className="text-[0.6rem] text-cobalt tracking-[0.28em] uppercase mt-3 font-display font-semibold">{DAYS[(d.getDay()+6)%7]}</p>
                </div>
                <Link to={`/calendario/${e.id}`} className="md:col-span-4 overflow-hidden block group" data-testid={`event-image-link-${e.id}`}>
                  <img src={resolveMedia(e.image)} alt={e.title} className="card-img w-full h-56 object-cover" />
                </Link>
                <div className="md:col-span-6">
                  <p className="eyebrow">{e.category}</p>
                  <Link to={`/calendario/${e.id}`} className="block" data-testid={`event-title-link-${e.id}`}>
                    <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-cobalt-deep hover:text-terracotta transition-colors">{e.title}</h3>
                  </Link>
                  <p className="mt-3 text-muted2 leading-relaxed">{e.description}</p>
                  <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-[0.62rem] font-display font-semibold tracking-[0.26em] uppercase text-cobalt">
                    <span className="inline-flex items-center gap-2"><Clock size={14}/> {e.time}</span>
                    <span className="inline-flex items-center gap-2"><MapPin size={14}/> {e.location}</span>
                    {e.price && <span>{e.price}</span>}
                  </div>
                  <div className="mt-6 flex flex-wrap items-center gap-5">
                    {hasCta(e) && (
                      <button
                        className="btn-primary inline-flex items-center gap-2"
                        onClick={(evt) => handleCta(e, evt)}
                        data-testid={`event-cta-${e.id}`}
                      >
                        {e.cta_label}
                        {e.cta_type === "external" && <ArrowUpRight size={14} />}
                      </button>
                    )}
                    <Link to={`/calendario/${e.id}`} className="text-[0.68rem] tracking-[0.26em] uppercase text-cobalt border-b border-cobalt pb-1 font-display font-semibold hover:text-terracotta hover:border-terracotta" data-testid={`event-more-${e.id}`}>
                      Más detalles
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
